import {
    getStoredFilters,
    usePersistFilterStorageKey,
    useSafeSessionStorage,
    useStorageKey,
} from '@/_shared/tools/storage.js';
import type { Column, Table } from '@tanstack/react-table';
import { type Dispatch, type SetStateAction, useCallback, useEffect } from 'react';
import { resetFilter } from '../../../_shared/reset.js';
import { DROPDOWN_DATE_OPTIONS } from '../../../date/component/data.js';
import { typedSetDateColumnFilterValue } from '../../../date/filter/index.js';
import { DATE_FILTER_OPERATION, type DateFilterValuesWithoutInArray } from '../../../date/filter/operation.js';
import type { StoredSelectedItemsSchema } from '../../../types.js';
import { useGetDateFilterData } from './data.js';
import { useOnDateSelectionChange } from './selection-change.js';

export function useDateListFilter<TData, TValue>({
    tableId,
    table,
    column,
    setDropdownValue,
    dataLimit = 5_000,
}: {
    tableId: string;
    table: Table<TData>;
    column: Column<TData, TValue>;
    setDropdownValue: Dispatch<SetStateAction<DateFilterValuesWithoutInArray['id']>>;
    dataLimit?: number;
}) {
    const disabled = table.options.data.length > dataLimit;

    const data = useGetDateFilterData({ disabled, table, column });

    const localStorageFilterKey = usePersistFilterStorageKey({
        id: tableId,
    });

    const sessionStorageKey = useStorageKey({
        base: tableId,
        utility: 'filter',
        id: column.id,
        version: 1,
    });

    const [selectedItems, setSelectedItems] = useSafeSessionStorage<StoredSelectedItemsSchema>(
        sessionStorageKey,
        () => ({
            current: data.allIds,
            persisted: data.allIds,
        })
    );

    const { handleSelectionChange } = useOnDateSelectionChange({ data, selectedItems, setSelectedItems });

    const resetListFilter = useCallback(
        (resetDropdown: boolean) => {
            if (disabled) {
                return;
            }

            resetFilter(column);

            setSelectedItems({
                current: data.allIds,
                persisted: data.allIds,
            });

            if (resetDropdown) {
                setDropdownValue(DROPDOWN_DATE_OPTIONS[0][0]);
            }
        },
        [column, data.allIds, disabled, setDropdownValue, setSelectedItems]
    );

    const updateSelectedDates = useCallback(
        (key: 'current' | 'persisted') => {
            if (disabled) {
                return;
            }

            const selectedValues = data.items
                .filter((item) => selectedItems[key].includes(item.id) && item.date)
                .map((item) => item.date)
                .filter((date) => date !== undefined);

            const items = selectedItems[key];

            setSelectedItems({
                current: items,
                persisted: items,
            });

            if (items.includes('select-all')) {
                if (!(key === 'persisted' && getStoredFilters(localStorageFilterKey).length)) {
                    resetListFilter(true);
                }
            } else {
                setDropdownValue(DATE_FILTER_OPERATION.inArray.id as DateFilterValuesWithoutInArray['id']);
                typedSetDateColumnFilterValue(column, {
                    operation: DATE_FILTER_OPERATION.inArray.id,
                    array: selectedValues,
                    includeBlanks: items.includes('select-blanks'),
                });
            }
        },
        [
            column,
            data.items,
            disabled,
            localStorageFilterKey,
            selectedItems,
            resetListFilter,
            setDropdownValue,
            setSelectedItems,
        ]
    );

    const onApplyFilter = useCallback(() => {
        updateSelectedDates('current');
    }, [updateSelectedDates]);

    // load the stored filters on initial load
    useEffect(() => {
        updateSelectedDates('persisted');
    }, []);

    return {
        disabled,
        data,
        selectedItems,
        handleSelectionChange,
        onApplyFilter,
        resetListFilter,
    };
}
